import { ArrowDown, ArrowUpRight, ChevronRight, FileText } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { Tooltip } from 'common-src/components/base';

import styles from './StepStrip.module.scss';

const StripPill = ({ step }) => {
  const { icon: Icon, label, count, href, jump, onClick, hasPopup, caption, unavailable } = step;
  const available = !unavailable;
  const why = unavailable ?? caption;
  const body = (
    <>
      <span className={styles.mark} aria-hidden="true">
        <Icon size={11} strokeWidth={2.5} />
      </span>
      <span className={styles.label}>{label}</span>
      {count !== undefined && <span className={styles.count}>{count}</span>}
      {href && available && <ArrowUpRight size={14} className={styles.arrow} aria-hidden="true" />}
      {jump && available && <ArrowDown size={14} className={styles.arrow} aria-hidden="true" />}
      <span className={styles.srOnly}>
        : {why}
        {href && available ? ' (opens in a new tab)' : ''}
      </span>
    </>
  );
  let pill;
  if (!available) {
    pill = (
      <button type="button" aria-disabled="true" className={`${styles.pill} ${styles.pillOff}`}>
        {body}
      </button>
    );
  } else if (href) {
    pill = (
      <a href={href} target="_blank" rel="noopener noreferrer" className={styles.pill}>
        {body}
      </a>
    );
  } else {
    pill = (
      <button
        type="button"
        aria-haspopup={hasPopup ? 'dialog' : undefined}
        onClick={onClick}
        className={styles.pill}
      >
        {body}
      </button>
    );
  }
  return (
    <Tooltip title={why} titleClassNames={['font-s-13', styles.why]}>
      {pill}
    </Tooltip>
  );
};

const stepShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  caption: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  jump: PropTypes.bool,
  count: PropTypes.number,
  unavailable: PropTypes.string,
  hasPopup: PropTypes.bool,
});

StripPill.propTypes = { step: stepShape.isRequired };

const StepStrip = ({ stages, draft }) => (
  <section aria-label="Steps for this visit" className={styles.strip}>
    <div className={styles.panel}>
      <div className={styles.stages}>
        {stages
          .filter(({ steps }) => steps.length > 0)
          .map(({ stage, steps }) => (
            <div key={stage} className={styles.stage}>
              <div className={styles.stageHead}>
                <h3 className={styles.stageName}>{stage}</h3>
                {stage === 'Coordinate' && <span className={styles.asNeeded}>as needed</span>}
                <span aria-hidden="true" className={styles.hairline} />
              </div>
              <ul className={styles.steps}>
                {steps.map((step) => (
                  <li key={step.key}>
                    <StripPill step={step} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
    </div>
    {draft && (
      <div className={styles.footer}>
        <span className={styles.footerTile} aria-hidden="true">
          <FileText size={17} />
        </span>
        <p className={styles.footerText}>
          <span className={styles.footerTitle}>{draft.title}</span>{' '}
          <span className={styles.footerDesc}>{draft.description}</span>
        </p>
        <button type="button" onClick={draft.onOpen} className={styles.openDraft}>
          Open the draft
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    )}
  </section>
);

StepStrip.propTypes = {
  stages: PropTypes.arrayOf(
    PropTypes.shape({
      stage: PropTypes.string.isRequired,
      steps: PropTypes.arrayOf(stepShape).isRequired,
    }),
  ).isRequired,
  draft: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    onOpen: PropTypes.func.isRequired,
  }),
};

export default StepStrip;
